import { Lock, MessagesSquare, Phone, ShieldAlert } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  CONVERSATION_TONES,
  useConversation,
  useConversationAssignees,
  useConversationLiveUpdates,
  useConversationAssignment,
  useConversationSpam,
  useConversationStatus,
} from '@/hooks/conversations';
import { api } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import {
  CONVERSATION_STATUS_LABELS,
  CONVERSATION_STATUS_VALUES,
  type ConversationDetail,
} from '@shared/schemas/conversation';
import { ConversationHistory, ConversationThread } from './ConversationThread';
import { ReplyBox } from './ReplyBox';

/*
 * One conversation, with everything you can do to it — ROADMAP.md §9.3 to §9.5.
 *
 * **The single renderer for a thread.** The inbox shows it in its right-hand
 * pane, `/admin/conversations/:id` shows it on its own, and 9.7 puts it on the
 * client record and the lead. A customer's history has to read identically
 * wherever it is opened, and one component is how that stays true.
 */

export function ConversationPane({ id, compact = false }: { id: string; compact?: boolean }) {
  const query = useConversation(id);

  /*
   * **A second admin watching this thread sees a reply appear** — the phase's
   * exit criterion. Safe to call from both here and the inbox: the hook opens
   * one `EventSource` per mounted component and closes it on unmount, and the
   * inbox mounts this pane rather than duplicating it.
   */
  useConversationLiveUpdates();

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <div className="h-24 animate-pulse rounded-lg bg-ink-100" />
        <div className="h-40 animate-pulse rounded-lg bg-ink-100" />
      </div>
    );
  }

  // A failed request is an error, never an empty state — CLAUDE.md §4 (P22).
  if (query.isError) {
    return (
      <div className="border-ink-200 rounded-lg border bg-white">
        <ErrorState
          title="That conversation could not be loaded"
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const detail = query.data;

  return (
    <div className="flex flex-col gap-4">
      <Header detail={detail} compact={compact} />

      {detail.isSpam ? (
        <p className="border-status-priority-border flex items-start gap-2 rounded-lg border bg-red-50 p-3 text-meta text-status-priority-text">
          <ShieldAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          Flagged as spam. It is hidden from the inbox but nothing has been deleted.
        </p>
      ) : null}

      <ConversationThread detail={detail} />

      <Composer detail={detail} />

      <details className="border-ink-200 rounded-lg border bg-white p-4">
        <summary className="cursor-pointer text-meta font-medium text-ink-900">History</summary>
        <div className="mt-3">
          <ConversationHistory detail={detail} />
          <p className="text-caption mt-3 text-ink-500">
            Started {formatInstant(detail.createdAt)}.
          </p>
        </div>
      </details>
    </div>
  );
}

/**
 * What you can add to a thread — **one box open, two behind buttons.**
 *
 * **This is the other half of the owner's 2026-08-08 note** that the screen read
 * as a ticketing system. Three stacked composers under every conversation is
 * three things to scroll past to reach the one anybody uses, and it is the
 * single biggest reason the page did not feel like somewhere you talk to a
 * customer.
 *
 * **What is emphatically unchanged is why there were three.** A reply, an
 * internal note and a logged call are still three components, calling three
 * endpoints, sharing no state, with no flag that turns one into another — the
 * cost of confusing them is a private remark landing in a customer's inbox.
 * Only one of them is *visible* by default. That is a layout decision; the
 * separation it sits on top of is a safety one, and collapsing them into a
 * single box with a mode switch would have been the mistake this comment
 * exists to stop somebody making later.
 *
 * The two buttons say what they do in words — "never sent", "not sent" — so the
 * distinction survives being one click away instead of always on screen.
 */
function Composer({ detail }: { detail: ConversationDetail }) {
  const [adding, setAdding] = useState<'note' | 'phone' | null>(null);

  return (
    <div className="flex flex-col gap-3">
      <ReplyBox detail={detail} />

      {adding === 'note' ? <NoteBox detail={detail} onDone={() => setAdding(null)} /> : null}
      {adding === 'phone' ? <PhoneLogBox detail={detail} onDone={() => setAdding(null)} /> : null}

      {adding === null ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setAdding('note');
            }}
          >
            <Lock aria-hidden="true" />
            Add an internal note
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setAdding('phone');
            }}
          >
            <Phone aria-hidden="true" />
            Log a phone call
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Header({ detail, compact }: { detail: ConversationDetail; compact: boolean }) {
  const status = useConversationStatus(detail.id);
  const assignment = useConversationAssignment(detail.id);
  const spam = useConversationSpam(detail.id);
  const assignees = useConversationAssignees();

  return (
    <section className="border-ink-200 rounded-lg border bg-white p-4">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-h3 text-ink-900">
            <MessagesSquare aria-hidden="true" className="size-5 shrink-0 text-bark-700" />
            <span className="truncate">{detail.subject}</span>
          </h2>
          <p className="text-caption mt-0.5 text-ink-500">
            {detail.reference} ·{' '}
            {compact ? (
              <Link
                to={`/admin/conversations/${detail.id}`}
                className="text-bark-700 underline underline-offset-2"
              >
                open on its own
              </Link>
            ) : (
              <>with {detail.contactName ?? detail.contactEmail}</>
            )}
          </p>
        </div>
        <StatusBadge tone={CONVERSATION_TONES[detail.status]} label={detail.statusLabel} />
      </div>

      {/*
        **A hiring thread says so, across the top, before anything else** —
        owner decision, 2026-08-07: "I would want it to be clear and obvious
        that they are not customers." A badge beside the status would read as
        one more status; a band across the thread reads as what kind of thing
        this is. It also says who can see it, because that is the surprising
        part and somebody will otherwise wonder why a colleague cannot find it.
      */}
      {detail.kind === 'applicant' ? (
        <p className="text-bark-800 mt-3 rounded-sm border border-honey-300 bg-honey-50 px-3 py-2 text-meta">
          <strong className="font-medium">{detail.kindLabel}</strong> — not a customer. Only super
          admins can see this conversation.
        </p>
      ) : null}

      <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-3">
        <div>
          <dt className="text-caption uppercase tracking-wide text-ink-500">With</dt>
          <dd className="mt-0.5 text-body text-ink-900">
            {detail.contactName ?? detail.contactEmail}
            {detail.contactName === null ? null : (
              <span className="block text-meta text-ink-500">{detail.contactEmail}</span>
            )}
          </dd>
        </div>
        {detail.kind === 'applicant' ? (
          <div>
            <dt className="text-caption uppercase tracking-wide text-ink-500">Application</dt>
            <dd className="mt-0.5 text-body text-ink-900">
              {/* The applicant pipeline, never the client record — pointing a
                  hiring thread at /admin/clients is the confusion the whole
                  marking exists to prevent. */}
              <Link to="/admin/applicants" className="text-bark-700 underline underline-offset-2">
                {detail.applicantName ?? 'Open the applicant pipeline'}
              </Link>
            </dd>
          </div>
        ) : (
          <div>
            <dt className="text-caption uppercase tracking-wide text-ink-500">Customer record</dt>
            <dd className="mt-0.5 text-body text-ink-900">
              {detail.clientId === null ? (
                <span className="text-ink-500">
                  {/* Not "none" — a thread nobody has filed is a job to do, not a
                      fact about the customer. */}
                  {detail.isTriage ? 'Not filed against anyone yet' : 'Not linked'}
                </span>
              ) : (
                <Link
                  to={`/admin/clients/${detail.clientId}`}
                  className="text-bark-700 underline underline-offset-2"
                >
                  {detail.clientName ?? 'Open their record'}
                </Link>
              )}
            </dd>
          </div>
        )}
        <div>
          <dt className="text-caption uppercase tracking-wide text-ink-500">Assigned to</dt>
          <dd className="mt-0.5">
            {/*
              **Assignment says whose job it is. It grants nothing** — every
              admin can already read every thread (business-rules.md §10).
            */}
            <select
              aria-label="Assigned to"
              value={detail.assignedToUserId ?? ''}
              disabled={assignment.isPending}
              onChange={(event) => {
                assignment.mutate(event.target.value === '' ? null : event.target.value);
              }}
              className="min-h-11 w-full rounded-sm border border-ink-300 bg-white px-2 text-body text-ink-900"
            >
              <option value="">Nobody</option>
              {(assignees.data?.assignees ?? []).map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </dd>
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {CONVERSATION_STATUS_VALUES.filter((value) => value !== detail.status).map((value) => (
          <Button
            key={value}
            variant="outline"
            size="sm"
            disabled={status.isPending}
            onClick={() => {
              status.mutate(value);
            }}
          >
            {CONVERSATION_STATUS_LABELS[value]}
          </Button>
        ))}

        {/* No delete anywhere — nothing in a conversation is hard-deleted. */}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          disabled={spam.isPending}
          onClick={() => {
            spam.mutate(!detail.isSpam);
          }}
        >
          {detail.isSpam ? 'Not spam' : 'Mark as spam'}
        </Button>
      </div>
    </section>
  );
}

/**
 * An internal note — never sent, and the control says so.
 *
 * Deliberately a different component from `ReplyBox`, calling a different
 * endpoint. They share no state and there is no toggle between them, because
 * the cost of confusing the two is an internal remark in a customer's inbox.
 */
function NoteBox({ detail, onDone }: { detail: ConversationDetail; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');

  const addNote = useMutation({
    mutationFn: (bodyText: string) =>
      api.post<ConversationDetail>(`/api/admin/conversations/${detail.id}/notes`, { bodyText }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['admin', 'conversations', detail.id], updated);
      setNote('');
      toast.success('Note added. Only the office can see it.');
      onDone();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return (
    <section className="rounded-lg border border-honey-300 bg-honey-50/60 p-4">
      <label
        htmlFor={`note-${detail.id}`}
        className="flex items-center gap-1.5 text-meta font-medium text-bark-900"
      >
        <Lock aria-hidden="true" className="size-4" />
        Internal note — never sent to the customer
      </label>
      <textarea
        id={`note-${detail.id}`}
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
        }}
        rows={3}
        placeholder="Something the office should know about this conversation."
        // No `focus:outline-none` — see the note on the same line in ReplyBox.
        // Tailwind's `outline-none` is a transparent outline that beats the
        // global `:focus-visible` ring, so this drew nothing when tabbed to.
        className="placeholder:text-ink-400 focus:border-bark-500 mt-2 w-full rounded-sm border border-ink-300 bg-white p-3 text-body text-ink-900"
      />
      <div className="mt-2 flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            addNote.mutate(note);
          }}
          disabled={note.trim().length === 0 || addNote.isPending}
        >
          {addNote.isPending ? 'Adding…' : 'Add note'}
        </Button>
      </div>
    </section>
  );
}

/**
 * A phone call, written down after the fact.
 *
 * **A third box rather than a mode on one of the other two**, for the reason
 * `NoteBox` gives above: the cost of confusing "send this to the customer" with
 * "record this internally" is a private remark landing in somebody's inbox, and
 * three components that share no state cannot be confused by a mistyped
 * boolean.
 *
 * What separates it from a note is not how it is stored but what it means. A
 * note is the office talking to itself; a call is the office talking to the
 * customer, so it counts as contact and moves "Last contact" on their record.
 */
function PhoneLogBox({ detail, onDone }: { detail: ConversationDetail; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [summary, setSummary] = useState('');

  const logCall = useMutation({
    mutationFn: (bodyText: string) =>
      api.post<ConversationDetail>(`/api/admin/conversations/${detail.id}/phone-logs`, {
        bodyText,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['admin', 'conversations', detail.id], updated);
      setSummary('');
      toast.success('Call logged.');
      onDone();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return (
    <section className="border-ink-200 rounded-lg border bg-white p-4">
      <label
        htmlFor={`phone-${detail.id}`}
        className="flex items-center gap-1.5 text-meta font-medium text-ink-900"
      >
        <Phone aria-hidden="true" className="size-4" />
        Log a phone call — not sent, but it counts as contact
      </label>
      <textarea
        id={`phone-${detail.id}`}
        value={summary}
        onChange={(event) => {
          setSummary(event.target.value);
        }}
        rows={3}
        placeholder="Rang about Thursday. Happy to move to the following week."
        className="placeholder:text-ink-400 focus:border-bark-500 mt-2 w-full rounded-sm border border-ink-300 bg-white p-3 text-body text-ink-900"
      />
      <div className="mt-2 flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            logCall.mutate(summary);
          }}
          disabled={summary.trim().length === 0 || logCall.isPending}
        >
          {logCall.isPending ? 'Saving…' : 'Log call'}
        </Button>
      </div>
    </section>
  );
}
